/**
 * Privacy Policy Page — Legacy Triangle
 */

import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Shield, Mail, Phone } from "lucide-react";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0f2d52]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0f2d52] via-[#1a3a5c] to-[#0f2d52]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#48d480]/10 border border-[#48d480]/30 rounded-full px-4 py-2 mb-6">
            <Shield className="w-4 h-4 text-[#48d480]" />
            <span className="text-[#48d480] text-sm font-medium">Your Privacy Matters</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Privacy Policy
          </h1>
          <p className="text-white/70 text-lg">Last updated: March 18, 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none" style={{ fontFamily: "'Inter', sans-serif" }}>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">1. Introduction</h2>
              <p className="text-gray-600 leading-relaxed">
                Legacy Triangle ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-semibold text-[#0f2d52] mb-3">Personal Information</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                <li>Fill out a contact form or application</li>
                <li>Subscribe to our newsletter</li>
                <li>Request information about our services</li>
                <li>Communicate with us via email, phone, or chat</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mb-4">
                This information may include:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Name, email address, and phone number</li>
                <li>Business name and business information</li>
                <li>Financial information relevant to funding applications</li>
                <li>Any other information you choose to provide</li>
              </ul>
            </div>

            <div className="mb-10">
              <h3 className="text-xl font-semibold text-[#0f2d52] mb-3">Automatically Collected Information</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                When you visit our website, we may automatically collect certain information, including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>IP address and browser type</li>
                <li>Device information and operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Process your funding applications and inquiries</li>
                <li>Provide, maintain, and improve our services</li>
                <li>Communicate with you about our services, updates, and promotions</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Monitor and analyze usage patterns and trends</li>
                <li>Protect against fraudulent or unauthorized activity</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We may share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>With Lending Partners:</strong> To process your funding applications</li>
                <li><strong>Service Providers:</strong> Third parties that help us operate our business</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                We do not sell your personal information to third parties for marketing purposes.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">5. SMS Communications and Mobile Messaging</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you provide your mobile phone number and opt in to receive SMS messages from Legacy Triangle Inc., you will receive text messages related to your funding inquiry, application status updates, account notifications, and appointment reminders. Message frequency may vary. Message and data rates may apply.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>Data Sharing:</strong> No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent will not be shared with any third parties.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Opt-Out:</strong> You can opt out at any time by replying STOP. For assistance, reply HELP.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">6. Data Security</h2>
              <p className="text-gray-600 leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">7. Your Rights and Choices</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Access, update, or delete your personal information</li>
                <li>Opt-out of marketing communications</li>
                <li>Request a copy of your data</li>
                <li>Withdraw consent where applicable</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">8. Cookies</h2>
              <p className="text-gray-600 leading-relaxed">
                We use cookies and similar tracking technologies to enhance your experience on our website. You can control cookies through your browser settings. For more information, please see our Cookie Policy.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">9. Third-Party Links</h2>
              <p className="text-gray-600 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">10. Children's Privacy</h2>
              <p className="text-gray-600 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child, we will take steps to delete such information.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">11. Changes to This Policy</h2>
              <p className="text-gray-600 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </div>

            <div className="bg-[#0f2d52]/5 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">12. Contact Us</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="space-y-3">
                <a href="mailto:info@legacytriangle.com" className="flex items-center gap-3 text-[#0f2d52] hover:text-[#48d480] transition-colors">
                  <Mail className="w-5 h-5 text-[#48d480]" />
                  info@legacytriangle.com
                </a>
                <a href="tel:+19844644672" className="flex items-center gap-3 text-[#0f2d52] hover:text-[#48d480] transition-colors">
                  <Phone className="w-5 h-5 text-[#48d480]" />
                  (984) 464-4672
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
